import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '@/components/common/PageHeader';
import { useToast } from '@/components/common/toast';
import { FilterBar, type FilterField } from '@/components/common/FilterBar';
import { AsyncButton } from '@/components/common/AsyncButton';
import { Select2 } from '@/components/forms/Select2';
import { Select2Multi } from '@/components/forms/Select2Multi';
import { DateRangePicker } from '@/components/forms/DateRangePicker';
import { useServerDataTable } from '@/components/tables/useServerDataTable';
import { DataGrid, type DataGridColumn } from '@/components/tables/DataGrid';
import { http } from '@/api/client/httpClient';
import { formatNumber } from '@/utils/format';
import { filterDataApi } from '@/api/services/filterData';
import { tradeBlotterApi } from '@/api/services/tradeBlotter';
import { useTradeBlotterLive } from './useTradeBlotterLive';
import type { TradeBlotterFilterInput, TradeBlotterRowDto } from '@/api/types/tradeBlotter';

function pad(n: number) {
  return String(n).padStart(2, '0');
}
function fmtDt(d: Date, endMinusMinute = false) {
  const date = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  if (endMinusMinute) return `${date}T23:59:00`;
  return `${date}T00:00:00`;
}
function todayStart() {
  return fmtDt(new Date());
}
function todayEnd() {
  return fmtDt(new Date(), true);
}

interface BlotterFilters {
  startDate: string;
  endDate: string;
  symbols: string[];
  entryType: string;
  side: string;
  connectorId: number | null;
  login: number | null;
  ticket: number | null;
}

function defaults(): BlotterFilters {
  return { startDate: todayStart(), endDate: todayEnd(), symbols: [], entryType: '', side: '', connectorId: null, login: null, ticket: null };
}

function toInput(f: BlotterFilters): TradeBlotterFilterInput {
  return {
    startDate: f.startDate,
    endDate: f.endDate,
    symbols: f.symbols.length ? f.symbols : [],
    entryType: f.entryType === '' ? null : Number(f.entryType),
    side: f.side === '' ? null : Number(f.side),
    connectorId: f.connectorId ?? null,
    login: f.login ?? null,
    ticket: f.ticket ?? null,
  };
}

const fmtTs = (iso: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};
const typeClass = (t: string) => (t === 'Buy' ? 'text-success' : t === 'Sell' ? 'text-danger' : 'text-muted');

export function TradeBlotterPage() {
  const toast = useToast();
  const [draft, setDraft] = useState<BlotterFilters>(defaults);
  const [applied, setApplied] = useState<BlotterFilters>(draft);

  const entryTypes = useQuery({ queryKey: ['rd-filter', 'tradeEntryTypes'], queryFn: filterDataApi.getTradeEntryTypes, staleTime: 5 * 60_000 });
  const sides = useQuery({ queryKey: ['rd-filter', 'tradeSides'], queryFn: filterDataApi.getTradeSides, staleTime: 5 * 60_000 });
  const connectors = useQuery({ queryKey: ['rd-filter', 'connectors'], queryFn: filterDataApi.getConnectors, staleTime: 5 * 60_000 });
  const instruments = useQuery({ queryKey: ['rd-filter', 'instruments'], queryFn: filterDataApi.getInstruments, staleTime: 5 * 60_000 });

  const appliedInput = useMemo(() => toInput(applied), [applied]);
  const table = useServerDataTable<TradeBlotterRowDto, TradeBlotterFilterInput>({
    queryKey: ['trade-blotter', 'rows'],
    fetcher: (req) => tradeBlotterApi.getRows(req),
    pageSize: 5,
    extraParams: appliedInput,
  });

  // Live refresh: reload only on the first page (mirrors reloadBlotterIfFirstPage).
  useTradeBlotterLive({
    enabled: true,
    onChanged: () => { if (table.pageIndex === 0) void table.refetch(); },
  });

  // 10s viewer ping.
  useEffect(() => {
    let active = true;
    const ping = () => { if (active) void tradeBlotterApi.pingViewing().catch(() => {}); };
    ping();
    const id = window.setInterval(ping, 10000);
    return () => { active = false; window.clearInterval(id); };
  }, []);

  // Connector warning when the window covers today (on load + each applied change).
  const lastWarnRef = useRef('');
  useEffect(() => {
    const key = `${applied.startDate}~${applied.endDate}`;
    if (lastWarnRef.current === key) return;
    lastWarnRef.current = key;
    tradeBlotterApi.getConnectorWarning(appliedInput).then((r) => { if (r?.message) toast.info(r.message); }).catch(() => {});
  }, [applied.startDate, applied.endDate, appliedInput, toast]);

  const fetch = () => {
    table.setPageIndex(0);
    setApplied({ ...draft });
  };
  const clear = () => {
    const d = defaults();
    setDraft(d);
    table.setPageIndex(0);
    setApplied(d);
  };

  async function exportExcel() {
    try {
      const res = await http.get(tradeBlotterApi.excelUrl, { params: appliedInput, paramsSerializer: { indexes: null }, responseType: 'blob' });
      const url = URL.createObjectURL(res.data as Blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'TradeBlotter.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to export the trade blotter.');
    }
  }

  const str = (v: string | number | null) => (v == null ? '' : String(v));

  const fields: (React.ReactNode | FilterField)[] = [
    {
      node: (
        <div key="date">
          <label className="form-label">Date Time Range</label>
          <DateRangePicker mode="range" enableTime timeSeconds maxDate={todayEnd()} value={{ start: draft.startDate, end: draft.endDate }} onChange={(v) => setDraft((f) => ({ ...f, startDate: v.start, endDate: v.end }))} />
        </div>
      ),
      pinned: true,
      width: 270,
    },
    <div key="symbols">
      <label className="form-label">Symbol</label>
      <Select2Multi placeholder="All" value={draft.symbols} options={instruments.data ?? []} onChange={(v) => setDraft((f) => ({ ...f, symbols: v }))} />
    </div>,
    <Select2 key="entry" label="Entry Type" allowClear placeholder="All" value={str(draft.entryType)} options={entryTypes.data ?? []} onChange={(v) => setDraft((f) => ({ ...f, entryType: v ?? '' }))} />,
    <Select2 key="side" label="Type" allowClear placeholder="All" value={str(draft.side)} options={sides.data ?? []} onChange={(v) => setDraft((f) => ({ ...f, side: v ?? '' }))} />,
    <Select2 key="connector" label="Connector" allowClear placeholder="All" value={str(draft.connectorId)} options={connectors.data ?? []} onChange={(v) => setDraft((f) => ({ ...f, connectorId: v ? Number(v) : null }))} />,
    <div key="login">
      <label className="form-label">Login</label>
      <input className="form-control" inputMode="numeric" value={draft.login ?? ''} onChange={(e) => setDraft((f) => ({ ...f, login: e.target.value ? Number(e.target.value) : null }))} />
    </div>,
    <div key="ticket">
      <label className="form-label">Ticket ID</label>
      <input className="form-control" inputMode="numeric" value={draft.ticket ?? ''} onChange={(e) => setDraft((f) => ({ ...f, ticket: e.target.value ? Number(e.target.value) : null }))} />
    </div>,
  ];

  const rows = table.rows;

  return (
    <div className="trade-blotter-page">
      <PageHeader title="Trade Blotter" breadcrumb={['Home', 'Trade Blotter']} />

      <FilterBar
        fields={fields}
        actions={
          <>
            <button type="button" className="btn btn-primary btn-sm" onClick={fetch} disabled={table.isFetching}>
              {table.isFetching ? <i className="fa fa-spinner fa-spin me-1" /> : <i className="fa fa-search me-1" />}Fetch
            </button>
            <button type="button" className="btn btn-link btn-sm" onClick={clear}>Clear All Filters</button>
            <AsyncButton className="btn btn-outline-primary btn-sm" icon="fa fa-download" onClick={exportExcel}>Excel</AsyncButton>
          </>
        }
      />

      <DataGrid
        columns={[
          { key: 'ts', title: 'Time', className: 'text-start', value: (r) => r.ts, cell: (r) => fmtTs(r.ts) },
          { key: 'login', title: 'Login', className: 'text-start', value: (r) => r.login },
          { key: 'symbol', title: 'Symbol', className: 'text-start', value: (r) => r.symbol },
          { key: 'ticket', title: 'Ticket', className: 'text-start', value: (r) => r.ticket },
          { key: 'type', title: 'Type', className: `text-start`, value: (r) => r.type, cell: (r) => <span className={typeClass(r.type)}>{r.type}</span> },
          { key: 'tradeType', title: 'Entry Type', className: 'text-start', value: (r) => r.tradeType },
          { key: 'volumeMt', title: 'Volume', className: 'text-end', value: (r) => r.volumeMt, cell: (r) => formatNumber(r.volumeMt, 2) },
          { key: 'price', title: 'Price', className: 'text-end', value: (r) => r.price, cell: (r) => formatNumber(r.price, Math.min(15, Math.max(0, r.digits))) },
          { key: 'swap', title: 'Swap', className: 'text-end', value: (r) => r.swap, cell: (r) => formatNumber(r.swap, 2) },
          { key: 'commission', title: 'Commission', className: 'text-end', value: (r) => r.commission, cell: (r) => formatNumber(r.commission, 2) },
          { key: 'profit', title: 'Profit / Loss', className: 'text-end', value: (r) => r.profit ?? null, cell: (r) => <span className={r.profit == null ? 'text-muted' : r.profit < 0 ? 'text-danger' : 'text-success'}>{r.profit == null ? '—' : formatNumber(r.profit, 2)}</span> },
          { key: 'reasonLabel', title: 'Reason', className: 'text-start', value: (r) => r.reasonLabel },
          { key: 'comment', title: 'Comment', className: 'text-start', value: (r) => r.comment },
          { key: 'connectorName', title: 'Source Connector', className: 'text-start', value: (r) => r.connectorName },
        ] as DataGridColumn<TradeBlotterRowDto>[]}
        data={rows}
        rowKey={(r) => `${r.ticket}-${r.connectorId}`}
        card
        emptyMessage={table.isLoading ? 'Loading…' : 'No data.'}
        server={{ pageIndex: table.pageIndex, pageSize: table.pageSize, pageCount: table.pageCount, totalCount: table.totalCount, onPageChange: table.setPageIndex, onPageSizeChange: table.setPageSize }}
      />
    </div>
  );
}
